// Step 25g: client-side dedup of TBTV-AI Keyword key_moments against
// TBTV-AI LLM key_moments. The keyword sublayer fires on every cue that
// matches a vocab pattern; the LLM sublayer emits wider, narrative-aware
// spans for the same play. The result is visual duplication in the
// editor: 2–3 rows for a single goal.
//
// This helper filters a list of moments so that a KW moment is dropped if
// any LLM moment for the SAME tbtv_id contains its start_ms in its
// [start_ms, end_ms] span. Both inputs and outputs are the same row
// shape; the only change is which rows survive.
//
// Behaviour notes:
//   - Only LLM rows from `source='tbtv_ai' AND detector='llm'` suppress.
//   - Only KW rows from `source='tbtv_ai' AND detector='keyword'` get
//     suppressed. Mux moments, manually-added moments, chapters, etc.
//     pass through unchanged.
//   - Step 26.9: suppression also requires SAME moment_type. A KW goal
//     can only be hidden by an LLM goal — never by an LLM structural /
//     penalty / shot. Previously the dedup ignored type, so a KW goal
//     could be silently suppressed by an unrelated LLM structural row
//     covering the same timestamp — that hid real goal calls from the
//     editor.
//   - Suppression is per-tbtv_id (multi-video reels won't cross-suppress).
//   - Strict containment: KW.start_ms must be >= LLM.start_ms AND <
//     LLM.end_ms. A KW row 1ms past the LLM out is NOT suppressed —
//     matches editorial intent of "this KW falls inside the LLM goal".
//   - LLM rows with a null end_ms are treated as having no span and
//     suppress nothing (defensive — shouldn't happen post-Step-22).
//   - To "see the raw data", the editor turns off the AI LLM source
//     filter chip in any of the views — no LLM rows in the input set
//     means no suppression, and KW rows reappear in full.

function isLlm(m) {
  return m && m.source === 'tbtv_ai' && m.detector === 'llm'
}

function isKw(m) {
  return m && m.source === 'tbtv_ai' && m.detector === 'keyword'
}

// Index LLM spans by tbtv_id, carrying moment_type so the type-aware
// suppression check can filter before scanning.
function indexLlmSpans(moments) {
  const byVideo = new Map()
  for (const m of moments) {
    if (!isLlm(m)) continue
    if (m.end_ms == null) continue
    const arr = byVideo.get(m.tbtv_id) || []
    arr.push({ start_ms: m.start_ms, end_ms: m.end_ms, moment_type: m.moment_type || null })
    byVideo.set(m.tbtv_id, arr)
  }
  return byVideo
}

function containsSameType(spans, ts, type) {
  if (!spans) return false
  for (const s of spans) {
    if (s.moment_type !== type) continue
    if (ts >= s.start_ms && ts < s.end_ms) return true
  }
  return false
}

// Returns a NEW array with suppressed KW rows removed.
export function dedupAiKeywordAgainstLlm(moments) {
  if (!Array.isArray(moments) || moments.length === 0) return moments || []
  const llmSpans = indexLlmSpans(moments)
  if (llmSpans.size === 0) return moments
  return moments.filter(m =>
    !(isKw(m) && containsSameType(llmSpans.get(m.tbtv_id), m.start_ms, m.moment_type || null))
  )
}
